/**
 * Tour System - Content Registry
 *
 * Central registry for all guided tour definitions.
 * Mirrors the help content pattern in content/help/index.ts.
 */

import type { PageTour } from './types';

// Page tour imports
import { dashboardTour } from './pages/dashboard';
import { projectsTour } from './pages/projects';
import { tasksTour } from './pages/tasks';
import { timesheetTour } from './pages/timesheet';
import { leaveTour } from './pages/leave';
import { trainingTour } from './pages/training';
import { knowledgeTour } from './pages/knowledge';
import { newsTour } from './pages/news';
import { directoryTour } from './pages/directory';
import { surveysTour } from './pages/surveys';
import { resourcesTour } from './pages/resources';
import { workLocationTour } from './pages/work-location';
import { analyticsTour } from './pages/analytics';
import { adminTour } from './pages/admin';
import { clientsTour } from './pages/clients';

// ============================================================================
// TOUR REGISTRY
// ============================================================================

/** All registered tours, keyed by tour ID */
const tourRegistry: Record<string, PageTour> = {};

/** Route-to-tour-ID mappings (most specific first) */
const routeMappings: Array<{ route: string; tourId: string; exact?: boolean }> = [];

function registerTour(tour: PageTour): void {
  tourRegistry[tour.id] = tour;
}

function mapRoute(route: string, tourId: string, exact: boolean = false): void {
  routeMappings.push({ route, tourId, exact });
}

// ============================================================================
// REGISTER ALL TOURS
// ============================================================================

[
  dashboardTour,
  projectsTour,
  tasksTour,
  timesheetTour,
  leaveTour,
  trainingTour,
  knowledgeTour,
  newsTour,
  directoryTour,
  surveysTour,
  resourcesTour,
  workLocationTour,
  analyticsTour,
  adminTour,
  clientsTour,
].forEach(registerTour);

// ============================================================================
// ROUTE MAPPINGS
// ============================================================================

// Dashboard
mapRoute('/', 'dashboard', true);

// Core pages
mapRoute('/projects', 'projects', true);
mapRoute('/tasks', 'tasks', true);
mapRoute('/my-tasks', 'tasks', true);
mapRoute('/timesheet', 'timesheet', true);
mapRoute('/leave', 'leave', true);
mapRoute('/training', 'training', true);
mapRoute('/knowledge', 'knowledge', true);
mapRoute('/news', 'news', true);
mapRoute('/directory', 'directory', true);
mapRoute('/surveys', 'surveys', true);
mapRoute('/resources', 'resources', true);
mapRoute('/work-location', 'work-location', true);
mapRoute('/analytics', 'analytics', true);
mapRoute('/admin', 'admin', true);
mapRoute('/clients', 'clients', true);

// ============================================================================
// LOOKUP FUNCTIONS
// ============================================================================

/**
 * Get the tour definition for a given route path
 */
export function getTourForRoute(pathname: string): PageTour | null {
  // Sort mappings by route length (most specific first)
  const sorted = [...routeMappings].sort((a, b) => b.route.length - a.route.length);

  // Try exact matches first
  for (const mapping of sorted) {
    if (mapping.exact && pathname === mapping.route) {
      return tourRegistry[mapping.tourId] || null;
    }
  }

  // Try prefix matches
  for (const mapping of sorted) {
    if (!mapping.exact && pathname.startsWith(mapping.route)) {
      return tourRegistry[mapping.tourId] || null;
    }
  }

  return null;
}

/**
 * Get a tour by its ID
 */
export function getTourById(id: string): PageTour | null {
  return tourRegistry[id] || null;
}

/**
 * Check whether a tour exists for the given route
 */
export function hasTourForRoute(pathname: string): boolean {
  return getTourForRoute(pathname) !== null;
}

// Re-export types
export type { TourStep, PageTour } from './types';
